import type {
  CreateIngredientCategoryPayload,
  IngredientCategoryDetailRow,
  IngredientCategoryListRow,
  UpdateIngredientCategoryPayload,
} from "@/modules/inventory/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type IngredientCategoriesListParams = {
  search?: string;
  isActive?: boolean;
};

export const inventoryIngredientCategoriesApi = {
  async list(
    params: IngredientCategoriesListParams = {},
  ): Promise<IngredientCategoryListRow[]> {
    const searchParams = new URLSearchParams();
    if (params.search?.trim()) {
      searchParams.set("search", params.search.trim());
    }
    if (params.isActive !== undefined) {
      searchParams.set("isActive", params.isActive ? "true" : "false");
    }
    const qs = searchParams.toString();
    return fetcher.get<IngredientCategoryListRow[]>(
      `${config.apiUrl}/inventory/ingredient-categories${qs ? `?${qs}` : ""}`,
    );
  },

  async lookup(): Promise<IngredientCategoryListRow[]> {
    return fetcher.get<IngredientCategoryListRow[]>(
      `${config.apiUrl}/inventory/ingredient-categories/lookup`,
    );
  },

  async get(id: string): Promise<IngredientCategoryDetailRow> {
    return fetcher.get<IngredientCategoryDetailRow>(
      `${config.apiUrl}/inventory/ingredient-categories/${id}`,
    );
  },

  async create(
    payload: CreateIngredientCategoryPayload,
  ): Promise<IngredientCategoryListRow> {
    return fetcher.post<IngredientCategoryListRow>(
      `${config.apiUrl}/inventory/ingredient-categories`,
      payload,
    );
  },

  async update(
    id: string,
    payload: UpdateIngredientCategoryPayload,
  ): Promise<IngredientCategoryListRow> {
    return fetcher.patch<IngredientCategoryListRow>(
      `${config.apiUrl}/inventory/ingredient-categories/${id}`,
      payload,
    );
  },

  async deactivate(id: string): Promise<IngredientCategoryListRow> {
    return fetcher.delete<IngredientCategoryListRow>(
      `${config.apiUrl}/inventory/ingredient-categories/${id}`,
    );
  },

  async reorder(orderedIds: string[]): Promise<{ updated: number }> {
    return fetcher.post<{ updated: number }>(
      `${config.apiUrl}/inventory/ingredient-categories/reorder`,
      { orderedIds },
    );
  },
};

export function formatIngredientCategoryApiError(e: unknown): string {
  const msg = (e as Error)?.message;
  if (msg === "DUPLICATE_CATEGORY_NAME") {
    return "ຊື່ຫມວດນີ້ມີແລ້ວ";
  }
  if (msg === "DUPLICATE_CATEGORY_CODE") {
    return "ລະຫັດຫມວດນີ້ມີແລ້ວ";
  }
  return msg ?? "ຄໍາຮ້ອງຂໍລົ້ມເຫຼວ";
}
